// src/components/BrandAudit/PublicAuditView.jsx
// PUBLIC route — NO auth guard
// Add to App.js OUTSIDE auth wrapper:
//   <Route path="/audit/view/:auditId" element={<PublicAuditView />} />

import React, { useEffect, useState } from 'react';
import { useParams }      from 'react-router-dom';
import { restoreAuditSnapshot } from './ShareButton';
import OverallScores       from './OverallScores';
import DomainAuthorityPage from './DomainAuthorityPage';
import PageSpeedPage       from './PageSpeedPage';
import TechnicalSEOPage    from './TechnicalSEOPage';
import GmbAuditPage        from './GmbAuditPage';
import AIVisibilityPage    from './AIVisibilityPage';
import FinalVerdict        from './FinalVerdict';
import ShareButton         from './ShareButton';
import '../../BrandPulse.css';
import { shareAPI } from '../../services/api';
const SHARE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// ── FIX 1: Use the same hardcoded key that GMBProfile uses so photos load ──
const GOOGLE_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  'AIzaSyBJtCMipuagFVjD0z0beDpzXDsYWsbR-tQ';

const getPhotoUrl = (name) =>
  `https://places.googleapis.com/v1/${name}/media?maxWidthPx=800&key=${GOOGLE_API_KEY}`;

const renderStars = (rating = 0) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return '★'.repeat(full) + (half ? '½' : '') + '☆'.repeat(5 - full - (half ? 1 : 0));
};

// ─────────────────────────────────────────────────────────────────────────────
// Read-only GMB summary card
// ─────────────────────────────────────────────────────────────────────────────
const GmbSummaryCard = ({ brandName, domain, location }) => {
  const [gmb, setGmb] = useState(null);

  useEffect(() => {
    const cleanedDomain = (domain || '')
      .replace(/^https?:\/\//, '').replace(/^www\./, '')
      .split('/')[0].toLowerCase().trim();
    const loc = location || 'noloc';

    const candidates = Object.keys(localStorage).filter(k =>
      k.startsWith('gmbBrand_') && k.endsWith(`_${loc}`)
    );

    let raw = null;
    const exactKey = `gmbBrand_${cleanedDomain}_${loc}`;
    raw = localStorage.getItem(exactKey);
    if (!raw && candidates.length) {
      raw = localStorage.getItem(candidates[0]);
    }

    if (!raw) return;
    try {
      const { data } = JSON.parse(raw);
      if (!data) return;

      const rating  = data.reviews?.value?.rating ?? data.reviews?.rating ?? 0;
      const reviews = data.reviews?.value?.count  ?? data.reviews?.count  ?? 0;
      const cat     = data.primary_category || data.gmb_categories?.[0] || '';
      const addr    = data.full_address || data.address || '';
      const photos  = Array.isArray(data.photos_sample) ? data.photos_sample : [];
      const phone   = data.phone_number?.display ?? data.phone_number?.value ?? data.phone ?? '';
      const website = data.website_url?.display  ?? data.website_url?.value  ?? data.website ?? '';
      const name    = data.business_name || brandName;

      setGmb({ name, cat, rating, reviews, addr, photos, phone, website });
    } catch {}
  }, [domain, location, brandName]);

  if (!gmb) return null;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: 20, marginBottom: 20 }}>
      {/* Photo strip */}
      {gmb.photos.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '140px 70px',
          gap: 2,
          background: '#e2e8f0',
        }}>
          <div style={{ gridColumn: 1, gridRow: '1/3', overflow: 'hidden' }}>
            <img
              src={getPhotoUrl(gmb.photos[0].name)}
              alt={gmb.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={e => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          {gmb.photos[1] && (
            <div style={{ overflow: 'hidden' }}>
              <img src={getPhotoUrl(gmb.photos[1].name)} alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            </div>
          )}
          {gmb.photos[2] && (
            <div style={{ overflow: 'hidden' }}>
              <img src={getPhotoUrl(gmb.photos[2].name)} alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="gmb-info">
        <div className="gmb-bname">{gmb.name}</div>
        {gmb.cat && <div className="gmb-bcat">{gmb.cat}</div>}

        <div className="rrow">
          <span className="rscore">{Number(gmb.rating).toFixed(1)}</span>
          <span className="stars">{renderStars(gmb.rating)}</span>
          <span className="rcount">{gmb.reviews.toLocaleString()} reviews</span>
        </div>

        <div className="divider" />

        {gmb.website && (
          <div className="irow-gmb">
            <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, fill: 'var(--muted)', flexShrink: 0 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            <a href={gmb.website} target="_blank" rel="noopener noreferrer">
              {gmb.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          </div>
        )}

        {gmb.phone && (
          <div className="irow-gmb">
            <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, fill: 'var(--muted)', flexShrink: 0 }}>
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            <span>{gmb.phone}</span>
          </div>
        )}

        {gmb.addr && (
          <div className="irow-gmb">
            <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, fill: 'var(--muted)', flexShrink: 0 }}>
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>{gmb.addr}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FIX 2: Read-only Competitors card
// Reads directly from competitors_list restored into localStorage
// ─────────────────────────────────────────────────────────────────────────────
const avatarColors = ['#E05C4B', '#0D9488', '#1A73E8', '#C47D1A', '#8B4FA0'];

const initials = (name) =>
  name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

const CompetitorsSummaryCard = ({ competitors }) => {
  if (!competitors || competitors.length === 0) return null;

  return (
    <div className="card" id="card-3" style={{ marginBottom: 20 }}>
      <h2 className="ctitle" style={{ marginBottom: 4 }}>Competitors in your space</h2>
      <p className="cdesc" style={{ marginBottom: 16 }}>
        Top competitors identified for this audit.
      </p>

      <div className="comp-list show">
        {competitors.map((comp, idx) => {
          const color       = avatarColors[idx % avatarColors.length];
          const displayName = comp.name || comp.website || 'Competitor';

          return (
            <div key={comp.id || idx} className="comp-row">
              <div className="comp-avatar" style={{ background: color }}>
                {initials(displayName)}
              </div>
              <div className="comp-info">
                <div className="comp-name-row">
                  <span className="comp-name">{displayName}</span>
                  {comp.isManual && (
                    <span style={{
                      fontSize: 10, fontWeight: 500,
                      background: 'rgba(107,79,160,.1)',
                      color: 'var(--thistle)',
                      padding: '2px 8px', borderRadius: 8, marginLeft: 6,
                    }}>
                      Added manually
                    </span>
                  )}
                </div>

                {/* Website */}
                {comp.website && (
                  <div className="comp-meta">
                    <a
                      className="comp-site"
                      href={comp.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
                      </svg>
                      {comp.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  </div>
                )}

                {/* Rating + reviews + city */}
                <div className="comp-meta" style={{ marginTop: 2 }}>
                  {comp.rating > 0 && (
                    <span className="comp-rating">
                      <span className="star">★</span>
                      {Number(comp.rating).toFixed(1)}
                    </span>
                  )}
                  {comp.reviews > 0 && (
                    <>
                      <span className="comp-divider" />
                      <span className="comp-reviews">{comp.reviews.toLocaleString()} reviews</span>
                    </>
                  )}
                  {(comp.city || comp.address) && (
                    <>
                      <span className="comp-divider" />
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {comp.city || comp.address?.split(',')[0]}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main PublicAuditView
// ─────────────────────────────────────────────────────────────────────────────
const PublicAuditView = () => {
  const { auditId } = useParams();
  const [status,    setStatus]    = useState('loading');
  const [auditMeta, setAuditMeta] = useState(null);

  // ── Silence all console output in the public/share view ──────────────
  useEffect(() => {
    const noop = () => {};
    const orig = {
      log:   console.log,
      warn:  console.warn,
      info:  console.info,
      debug: console.debug,
    };
    console.log   = noop;
    console.warn  = noop;
    console.info  = noop;
    console.debug = noop;
    return () => {
      console.log   = orig.log;
      console.warn  = orig.warn;
      console.info  = orig.info;
      console.debug = orig.debug;
    };
  }, []);

//   useEffect(() => {
//     if (!auditId) { setStatus('error'); return; }

//     try {
//       const raw = localStorage.getItem(`shared_audit_${auditId}`);
//       if (!raw) { setStatus('expired'); return; }

//       const { ts, data } = JSON.parse(raw);

//       if (Date.now() - ts > SHARE_TTL_MS) {
//         localStorage.removeItem(`shared_audit_${auditId}`);
//         setStatus('expired');
//         return;
//       }

//       // Restore all audit cache keys into localStorage
//       restoreAuditSnapshot(data);

//       // Extract meta
//       const fd          = data['auditFormData'] || {};
//       const competitors = data['competitors_list'] || [];

//       setAuditMeta({
//         brandName:   fd.brandName      || '',
//         websiteUrl:  fd.websiteUrl     || '',
//         location:    fd.locations?.[0]?.city || '',
//         placeId:     fd.placeId        || '',
//         tags:        fd.pillarKeywords || [],
//         competitors,
//       });

//       setStatus('ready');
//     } catch (e) {
//       console.error('PublicAuditView restore error:', e);
//       setStatus('error');
//     }
//   }, [auditId]);

    useEffect(() => {
  if (!auditId) { setStatus('error'); return; }

  shareAPI.getSnapshot(auditId)
    .then(res => {
      const { snapshot } = res.data;
      restoreAuditSnapshot(snapshot);

      const fd = snapshot['auditFormData'] || {};
      setAuditMeta({
        brandName:   fd.brandName            || '',
        websiteUrl:  fd.websiteUrl           || '',
        location:    fd.locations?.[0]?.city || '',
        placeId:     fd.placeId              || '',
        tags:        fd.pillarKeywords       || [],
        competitors: snapshot['competitors_list'] || [],
      });
      setStatus('ready');
    })
    .catch(() => setStatus('error'));
}, [auditId]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px', width: 32, height: 32 }} />
          <div style={{ fontSize: 15, color: '#6B4FA0' }}>Loading audit report…</div>
        </div>
      </div>
    );
  }

  // ── Expired / Error ──────────────────────────────────────────────────────
  if (status === 'expired' || status === 'error') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontFamily: "'DM Sans', sans-serif",
        padding: 20,
      }}>
        <div style={{
          textAlign: 'center', maxWidth: 400,
          background: 'rgba(255,255,255,0.85)',
          borderRadius: 20, padding: '40px 32px',
          border: '1px solid rgba(107,79,160,0.15)',
          boxShadow: '0 8px 32px rgba(107,79,160,0.1)',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>
            {status === 'expired' ? '⏰' : '⚠️'}
          </div>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, color: '#1C1626', marginBottom: 10 }}>
            {status === 'expired' ? 'Link expired' : 'Report not found'}
          </h2>
          <p style={{ fontSize: 14, color: '#8B82A0', lineHeight: 1.6, marginBottom: 24 }}>
            {status === 'expired'
              ? 'This audit link has expired (valid for 7 days). Please ask for a new link.'
              : 'This report could not be found. The link may be incorrect or already cleared.'}
          </p>
          <a href="/" style={{
            display: 'inline-block',
            background: '#6B4FA0', color: 'white',
            padding: '10px 24px', borderRadius: 10,
            fontFamily: "'DM Sans',sans-serif",
            fontSize: 14, fontWeight: 600, textDecoration: 'none',
          }}>
            Run a new audit
          </a>
        </div>
      </div>
    );
  }

  // ── Ready ────────────────────────────────────────────────────────────────
  const {
    brandName = '', websiteUrl = '', location = '',
    tags = [], placeId = '', competitors = [],
  } = auditMeta || {};

  return (
    <div className="wrap">
      <style>{`
        /* ── READ-ONLY VIEW — lock down all interactive elements ── */

        /* 1. Hide all continue/action buttons inside audit cards */
        #card-4 .btn, #card-5 .btn, #card-6 .btn,
        #card-7 .btn, #card-8 .btn, #card-9 .btn,
        #card-10 .btn { display: none !important; }

        /* 2. Hide competitor edit/delete/add controls */
        .comp-actions,
        .add-comp-btn,
        .edit-actions { display: none !important; }

        /* 3. Hide GMB action buttons (Directions, View on Google, Reviews) */
        .gabtn { display: none !important; }

        /* 4. Hide hours toggle button — show hours inline instead */
        .hours-toggle { display: none !important; }
        .hours-detail { display: block !important; }

        /* 5. Hide map interactive controls (keyword dropdown, radius slider, reset pin) */
        .map-controls { display: none !important; }

        /* 6. Hide AI visibility controls (keyword dropdown row, platform tabs) */
        .ai-kw-row  { display: none !important; }
        .ai-tabs    { display: none !important; }

        /* 7. Make all select/input/range elements non-interactive */
        select, input[type="range"], input[type="text"],
        input[type="number"], input[type="email"] {
          pointer-events: none !important;
          user-select: none !important;
          cursor: default !important;
        }

        /* 8. Disable map pin dragging visually */
        .map-outer { pointer-events: none !important; }
        /* But allow scroll/zoom by re-enabling the map iframe itself */
        .map-outer iframe,
        .map-outer canvas { pointer-events: auto !important; }

        /* 9. Hide TTS play buttons (audio makes no sense in a share view) */
        .play-btn { display: none !important; }

        /* 10. Hide any refresh/retry/regenerate buttons */
        .btn-refresh,
        .retry-btn { display: none !important; }

        /* 11. Share FAB stays fully clickable */
        .share-fab,
        .share-fab * { pointer-events: all !important; }

        /* 12. Show the static keyword labels we added for PDF/share */
        .ai-kw-print-label,
        .map-kw-print-label {
          display: block !important;
          font-size: 13px;
          font-weight: 600;
          color: #6B4FA0;
          margin-bottom: 12px;
          padding: 6px 12px;
          background: rgba(107,79,160,0.07);
          border-radius: 8px;
        }
      `}</style>

      <header className="hdr">
        <h1 className="headline">Brand <em>Audit</em> Report</h1>
        <p className="sub">{brandName}{location ? ` · ${location}` : ''}</p>
      </header>

      {/* ── GMB summary card (replaces wizard cards 1 & 2) ── */}
      <GmbSummaryCard
        brandName={brandName}
        domain={websiteUrl}
        location={location}
      />

      {/* ── FIX 2: Competitors read-only card ── */}
      <CompetitorsSummaryCard competitors={competitors} />

      {/* ── Audit report cards (read-only) ── */}
      <div id="card-4" className="card">
        <OverallScores
          brandName={brandName}
          websiteUrl={websiteUrl}
          location={location}
          competitors={competitors}
          onContinue={() => {}}
        />
      </div>

      <div id="card-5" className="card">
        <DomainAuthorityPage
          brandName={brandName}
          websiteUrl={websiteUrl}
          competitors={competitors}
          onContinue={() => {}}
        />
      </div>

      <div id="card-6" className="card">
        <PageSpeedPage
          brandName={brandName}
          websiteUrl={websiteUrl}
          competitors={competitors}
          onContinue={() => {}}
        />
      </div>

      <div id="card-7" className="card">
        <TechnicalSEOPage
          websiteUrl={websiteUrl}
          onContinue={() => {}}
        />
      </div>

      <div id="card-8" className="card">
        <GmbAuditPage
          brandName={brandName}
          websiteUrl={websiteUrl}
          location={location}
          placeId={placeId}
          competitors={competitors}
          onContinue={() => {}}
        />
      </div>

      <div id="card-9" className="card">
        <AIVisibilityPage
          brandName={brandName}
          pillarKeywords={tags}
          location={location}
          onContinue={() => {}}
        />
      </div>

      <div id="card-10" className="card">
        <FinalVerdict />
      </div>

      <ShareButton brandName={brandName} />
    </div>
  );
};

export default PublicAuditView;
